// Administracion -> Grupos (D34): los equipos que atienden casos, como los grupos de Zammad (Soporte, Facturacion,
// Comercial, Sip Caller...). Cada grupo tiene sus personas y, si quiere, su buzon (helpdesk@, xventas@...): quien es
// del grupo ve ese buzon en su Bandeja y responde desde el con su firma; si "abre casos", cada correo nuevo es un caso.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { type Buzon, compartirBuzon } from '../../api/correo';
import { borrarBuzonGrupo, type BuzonGrupo, buzonesGrupo, crearBuzonGrupo, editarBuzonGrupo } from '../../api/correoEstado';
import { renombrarGrupoDeCasos } from '../../api/casos';
import { renombrarGrupoEnVistas } from '../../api/vistasCaso';
import { useMiembros } from '../../data/queries';
import { Icon } from '../../lib/icons';
import { Empty, ErrorBox, Pill, Skeleton } from '../../ui/bits';
import { BuzonDialog, estadoBuzon, useBuzones } from '../../ui/Buzones';
import { Dialog } from '../../ui/Dialog';
import { toast } from '../../ui/toasts';

export const useBuzonesGrupo = () => useQuery({ queryKey: ['buzones-grupo'], queryFn: buzonesGrupo, staleTime: 60_000 });

export function BuzonesGrupo() {
  const grupos = useBuzonesGrupo();
  const cuentas = useBuzones();
  const miembros = useMiembros();
  const [editar, setEditar] = useState<BuzonGrupo | 'nuevo' | null>(null);
  const [conectar, setConectar] = useState(false);
  const cuentaDe = (g: BuzonGrupo) => (cuentas.data ?? []).find((c) => c.id === g.cuentaId);
  const nombre = (id: string) => miembros.data?.find((m) => m.id === id)?.nombre ?? '—';
  const lista = [...(grupos.data ?? [])].sort((a, b) => Number(b.activo) - Number(a.activo) || a.nombre.localeCompare(b.nombre));

  return (
    <section className="box">
      <div className="box-h">
        <h2 className="lbl">Grupos</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setConectar(true)}>
            <Icon name="mail" /> Conectar buzón
          </button>
          <button className="btn primary" onClick={() => setEditar('nuevo')}>
            <Icon name="plus" /> Nuevo grupo
          </button>
        </div>
      </div>
      <p className="note">
        Un grupo es un equipo que atiende casos (como en Zammad). Sus personas ven los casos del grupo en Bandeja → Casos y, si el grupo tiene buzón, también sus correos, y responden desde él. Para usar un buzón, primero conéctalo con su usuario y clave.
      </p>
      {grupos.error ? <ErrorBox error={grupos.error} onRetry={() => grupos.refetch()} /> : null}
      {grupos.isLoading ? (
        <Skeleton rows={4} />
      ) : lista.length ? (
        <div className="rows">
          {lista.map((g) => {
            const c = cuentaDe(g);
            const e = c ? estadoBuzon(c) : null;
            return (
              <button key={g.id} className="row act-row tarea-abrir" onClick={() => setEditar(g)} style={g.activo ? undefined : { opacity: 0.55 }}>
                <span className="ico-dot">
                  <Icon name={g.cuentaId ? 'inbox' : 'users'} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="nm" style={{ display: 'block' }}>
                    {g.nombre}
                    {g.correo ? ` · ${g.correo}` : ''}
                  </span>
                  <span className="sb" style={{ display: 'block' }}>
                    {g.miembros.length ? g.miembros.map(nombre).join(', ') : 'Sin personas asignadas'}
                    {g.cuentaId && g.creaCasos ? ' · cada correo abre un caso' : ''}
                    {g.activo ? '' : ' · inactivo'}
                  </span>
                </span>
                {!g.cuentaId ? (
                  <Pill>Sin buzón</Pill>
                ) : e ? (
                  <Pill tone={e.tono} dot>
                    {e.texto}
                  </Pill>
                ) : (
                  <Pill tone="warn">Buzón sin conectar</Pill>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <Empty icon="users" titulo="Aún no hay grupos" texto="Crea los equipos que atienden casos: Soporte, Facturación, Comercial…" />
      )}
      {conectar ? <BuzonDialog onClose={() => setConectar(false)} /> : null}
      {editar ? <GrupoDialog g={editar === 'nuevo' ? null : editar} cuentas={cuentas.data ?? []} onClose={() => setEditar(null)} /> : null}
    </section>
  );
}

function GrupoDialog({ g, cuentas, onClose }: { g: BuzonGrupo | null; cuentas: Buzon[]; onClose: () => void }) {
  const qc = useQueryClient();
  const miembros = useMiembros();
  // Un grupo puede tener el correo de un buzon que este usuario no ve (el personal de otra persona, p. ej. "Jahir" ->
  // jlmoreno@): se muestra igual para no borrarlo al guardar. Su valor es "propio:<correo>" si no tiene cuenta.
  const opciones: { id: string; correo: string; compartido: boolean }[] = [...cuentas];
  if (g?.correo && !cuentas.some((c) => c.id === g.cuentaId || c.correo.toLowerCase() === g.correo.toLowerCase()))
    opciones.push({ id: g.cuentaId ?? `propio:${g.correo}`, correo: g.correo, compartido: false });
  const inicial = g?.cuentaId ?? (g?.correo ? (opciones.find((c) => c.correo.toLowerCase() === g.correo.toLowerCase())?.id ?? '') : '');
  const [v, setV] = useState({ nombre: g?.nombre ?? '', cuentaId: inicial, miembros: g?.miembros ?? [], firma: g?.firma ?? '', creaCasos: g?.creaCasos ?? true, activo: g?.activo ?? true });
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const cuenta = opciones.find((c) => c.id === v.cuentaId);
  const cuentaReal = cuenta && !cuenta.id.startsWith('propio:') ? cuenta.id : null;
  // Un buzon con el correo de alguien del equipo es personal (D38, D62, D83): puede ir en un grupo para abrir casos, pero
  // no se comparte: el correo lo lee solo su dueno y los casos de ese grupo los ve solo su dueno.
  const duenoDe = new Map((miembros.data ?? []).filter((m) => m.email).map((m) => [String(m.email).trim().toLowerCase(), m.nombre]));
  const duenoPersonal = (c: { correo: string }) => duenoDe.get(c.correo.trim().toLowerCase());
  const alternar = (id: string) => setV((x) => ({ ...x, miembros: x.miembros.includes(id) ? x.miembros.filter((m) => m !== id) : [...x.miembros, id] }));
  const listo = () => {
    for (const k of ['buzones-grupo', 'buzones', 'casos', 'casos-vista', 'vistas-conteo']) void qc.invalidateQueries({ queryKey: [k] });
    onClose();
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const nombre = v.nombre.trim() || cuenta?.correo || '';
    if (!nombre) return toast('Ponle un nombre al grupo.', 'info');
    setGuardando(true);
    try {
      if (cuentaReal) await compartirBuzon(cuentaReal, !duenoPersonal(cuenta!));
      const datos = { nombre, correo: cuenta?.correo ?? '', cuentaId: cuentaReal, miembros: v.miembros, firma: v.firma, creaCasos: !!cuenta && v.creaCasos, activo: v.activo };
      if (g) await editarBuzonGrupo(g.id, datos);
      else await crearBuzonGrupo(datos);
      // Renombrar el grupo lleva sus casos al nombre nuevo.
      if (g && g.nombre !== nombre) {
        const n = await renombrarGrupoDeCasos(g.nombre, nombre);
        const vs = await renombrarGrupoEnVistas(g.nombre, nombre);
        if (n || vs) toast(`${n} casos y ${vs} vistas pasaron a "${nombre}".`);
      }
      // Si se cambio o se quito el buzon, el anterior vuelve a ser personal.
      if (g?.cuentaId && g.cuentaId !== cuentaReal) await compartirBuzon(g.cuentaId, false).catch(() => {});
      toast('Grupo guardado.');
      listo();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  const borrar = async () => {
    if (!g) return;
    if (!confirmar) return setConfirmar(true);
    try {
      if (g.cuentaId) await compartirBuzon(g.cuentaId, false).catch(() => {});
      await borrarBuzonGrupo(g.id);
      toast('Grupo borrado. Sus casos conservan el nombre del grupo.');
      listo();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <Dialog titulo={g ? g.nombre : 'Nuevo grupo'} onClose={onClose} width={620}>
      <form className="form" onSubmit={guardar}>
        <div className="grid2">
          <label>
            Nombre del grupo
            <input id="bg-nombre" value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} placeholder="Soporte" required />
          </label>
          <label>
            Buzón (opcional)
            <select id="bg-cuenta" value={v.cuentaId} onChange={(e) => setV({ ...v, cuentaId: e.target.value })}>
              <option value="">Sin buzón</option>
              {opciones.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.correo}
                  {duenoPersonal(c) ? ` (personal de ${duenoPersonal(c)})` : c.compartido ? ' (compartido)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        {cuenta && duenoPersonal(cuenta) ? (
          <p className="note">
            Correo personal de {duenoPersonal(cuenta)}: no se comparte. Sus correos y los casos de este grupo los ve solo {duenoPersonal(cuenta)}, aunque marques a otras personas.
          </p>
        ) : null}
        <fieldset className="srv">
          <legend>Personas del grupo</legend>
          <div className="chk-lista">
            {(miembros.data ?? []).map((m) => (
              <label key={m.id} className="chk">
                <input type="checkbox" checked={v.miembros.includes(m.id)} onChange={() => alternar(m.id)} />
                {m.nombre}
              </label>
            ))}
          </div>
        </fieldset>
        {cuenta ? (
          <label className="chk">
            <input type="checkbox" checked={v.creaCasos} onChange={(e) => setV({ ...v, creaCasos: e.target.checked })} />
            Cada correo nuevo a {cuenta.correo} abre un caso numerado (como un ticket de Zammad)
          </label>
        ) : null}
        <label className="chk">
          <input type="checkbox" checked={v.activo} onChange={(e) => setV({ ...v, activo: e.target.checked })} />
          Grupo activo (los inactivos no aparecen para elegir)
        </label>
        {cuenta ? <p className="note">La firma de este buzón se edita en Administración → Plantillas y firmas.</p> : null}
        <div className="actions">
          {g ? (
            <button type="button" className={`btn ${confirmar ? 'danger' : 'ghost'}`} onClick={borrar} style={{ marginRight: 'auto' }}>
              {confirmar ? 'Sí, borrar el grupo' : 'Borrar grupo'}
            </button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
